// routes/ThreadsPane.jsx — 대화 오브젝트의 목록.
//
// 대화는 Space 안에서 실제 일이 시작되는 자리다. 이 목록은 탐색용이고, 항목을 열면
// 바로 기록과 작성창이 있는 대화면으로 간다. 화면이 서버의 구현 세부를 설명하지 않는다.

import { read } from '../api/client.js';
import { useResource } from '../api/resource.js';
import { useSession } from '../session/session.jsx';
import { ButtonLink, Icon, IconChip, List, ListRow, PageHead } from '../ui/primitives.jsx';
import { Empty, Resource } from '../ui/states.jsx';
import { useNativeConversationCapabilities } from './conversation-capabilities.js';

export function ThreadsPane({ spaceId, archived }) {
  const session = useSession();
  const threads = useResource(`space:${spaceId}:threads`, () =>
    read('conversation-list', { nodeId: session.nodeId, query: { spaceId } }),
  );
  const conversation = useNativeConversationCapabilities(spaceId);
  const canCreate = !archived && conversation.canAppend;

  return (
    <section class="work work--threads">
      <div class="work__crumb t-meta">
        <IconChip object="thread" name="thread" />
        <span>이 Space의 대화</span>
      </div>

      <PageHead
        title="대화"
        meta="주제를 열고, 원문 대화를 이어 갑니다. 필요한 판단만 Task로 만듭니다."
        actions={canCreate ? (
          <ButtonLink size="sm" href={`/s/${spaceId}/t/new`}>
            <Icon name="plus" />
            새 대화
          </ButtonLink>
        ) : !archived && conversation.resource.status === 'ready' ? (
          <p class="t-meta">이 연결에서는 새 대화를 열 수 없습니다.</p>
        ) : !archived && conversation.resource.status === 'error' ? (
          <p class="t-meta">대화 연결 권한을 확인하지 못했습니다. 다시 읽은 뒤에 새 대화를 열 수 있습니다.</p>
        ) : null}
      />

      <div class="work__body threads-pane">
        <Resource resource={threads} loadingLabel="대화를 읽는 중">
          {(data) => {
            const list = data.threads ?? [];
            if (list.length === 0) {
              return (
                <Empty title="아직 대화가 없습니다.">
                  첫 대화를 열면 이 자리에 쌓입니다.
                </Empty>
              );
            }
            return (
              <section class="threads-pane__directory" aria-label="최근 대화">
                <header class="threads-pane__directory-head">
                  <h2>최근 대화</h2>
                  <span class="t-meta">{list.length}</span>
                </header>
                <List class="threads-pane__list">
                  {list.map((t) => (
                    <ListRow key={t.threadId} href={`/s/${spaceId}/t/${t.threadId}`}>
                      <IconChip object="thread" name="thread" />
                      <span class="grow">
                        <span class="t-body-strong">{t.topic}</span>
                        <span class="threads-pane__row-meta t-meta">원문 대화 · 기록 {t.lastSequence}</span>
                      </span>
                      {t.state === 'closed' ? <span class="t-meta">닫힘</span> : null}
                      <Icon name="arrow" class="threads-pane__arrow" aria-hidden="true" />
                    </ListRow>
                  ))}
                </List>
              </section>
            );
          }}
        </Resource>
      </div>
    </section>
  );
}
